/**
 * ORBE : une sphere de verre teintee a la couleur du client, dont le reflet derive lentement.
 *
 * L'IDEE, EN UN MOT : un objet, pas une icone. La sphere respire, le reflet se deplace tout seul,
 * et rien de tout cela ne demande de decision au client — une seule couleur suffit.
 *
 * AU TEMPS FORT, une bulle de dialogue surgit en haut a droite et la sphere a un frisson.
 * A L'OUVERTURE, la sphere se recueille et une croix de verre se deploie dedans, le reflet
 * continue de deriver : c'est le meme objet, pas un second bouton.
 *
 * CE FICHIER N'IMPORTE RIEN, PAR CONTRAT. Un look doit pouvoir etre lu, evalue et rendu hors du
 * SDK — c'est ce qui rend la page d'essai capable de montrer le code livre et non une copie.
 * Sa feuille est un gabarit de chaine : deux accents graves dans le fichier, pas un de plus.
 */
export default {
  id: 'orbe',

  css: `
.woot-lanceur[data-look="orbe"] .woot-orbe__disque {
  position: absolute;
  inset: 0;
  border-radius: 50%;
  background: var(--client);
  background: radial-gradient(circle at 34% 28%, color-mix(in srgb, var(--client) 45%, white) 0%, var(--client) 48%, color-mix(in srgb, var(--client) 62%, black) 100%);
  box-shadow: 0 12px 30px rgba(16, 19, 23, .28);
  box-shadow: 0 12px 30px color-mix(in srgb, var(--client) 38%, transparent);
  transition: transform .5s cubic-bezier(.2, .8, .2, 1), filter .3s ease;
}

.woot-lanceur[data-look="orbe"][data-etat="repos"] .woot-orbe__disque { animation: woot-orbe-respire 4.5s ease-in-out infinite; }
@keyframes woot-orbe-respire { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.035); } }

.woot-lanceur[data-look="orbe"] .woot-orbe__reflet {
  position: absolute;
  width: 22px;
  height: 13px;
  left: 12px;
  top: 10px;
  border-radius: 50%;
  background: rgba(255, 255, 255, .78);
  filter: blur(2px);
  animation: woot-orbe-derive 7s ease-in-out infinite;
  transition: transform .4s ease;
}

.woot-lanceur[data-look="orbe"] .woot-orbe__reflet--bas {
  width: 26px;
  height: 8px;
  left: 24px;
  top: auto;
  bottom: 9px;
  background: rgba(255, 255, 255, .22);
  filter: blur(3px);
  animation: none;
}

@keyframes woot-orbe-derive {
  0%, 100% { transform: translate(0, 0); }
  33% { transform: translate(6px, 3px); }
  66% { transform: translate(-3px, 5px); }
}

.woot-lanceur[data-look="orbe"]:hover .woot-orbe__disque { filter: brightness(1.08); }
.woot-lanceur[data-look="orbe"]:hover .woot-orbe__reflet { transform: translate(-4px, -4px) scale(1.25); }

/* LE TEMPS FORT : un frisson, et la bulle de dialogue surgit du coin. */
.woot-lanceur[data-look="orbe"][data-etat="attention"] .woot-orbe__disque {
  animation: woot-orbe-respire 4.5s ease-in-out infinite, woot-orbe-frisson .7s ease-out;
}

@keyframes woot-orbe-frisson {
  0% { transform: scale(1); }
  40% { transform: scale(.94, 1.06); }
  100% { transform: scale(1); }
}

.woot-lanceur[data-look="orbe"] .woot-orbe__accent {
  position: absolute;
  left: calc(100% - 16px);
  bottom: calc(100% - 16px);
  opacity: 0;
  transform: scale(.4);
  transform-origin: bottom left;
  transition: transform .4s cubic-bezier(.34, 1.56, .64, 1), opacity .25s ease;
}

.woot-lanceur[data-look="orbe"][data-etat="attention"] .woot-orbe__accent { opacity: 1; transform: scale(1); }

/* Le cartouche d'Orbe est du verre lui aussi : la surface du theme, allegee, jamais du blanc pose
   sur une page dont on ne sait rien. Le repli sert les navigateurs sans color-mix. */
.woot-lanceur[data-look="orbe"] .woot-lanceur__cartouche {
  background: var(--woot-surface);
  background: color-mix(in srgb, var(--woot-surface) 72%, transparent);
  backdrop-filter: blur(10px);
}

/* OUVERT : la sphere se recueille, la croix de verre se deploie dedans. */
.woot-lanceur[data-look="orbe"] .woot-lanceur__croix {
  width: 24px;
  height: 24px;
  margin: -12px;
}

.woot-lanceur[data-look="orbe"] .woot-lanceur__croix::before,
.woot-lanceur[data-look="orbe"] .woot-lanceur__croix::after {
  height: 2.2px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, .18), 0 0 6px rgba(255, 255, 255, .35);
}

.woot-lanceur[data-look="orbe"][data-etat="ouvert"] .woot-orbe__disque {
  animation: none;
  transform: scale(.92);
  filter: brightness(1.04);
}
`,

  monter: conteneur => {
    const disque = document.createElement('div');
    disque.className = 'woot-orbe__disque';
    const reflet = document.createElement('span');
    reflet.className = 'woot-orbe__reflet';
    const refletBas = document.createElement('span');
    refletBas.className = 'woot-orbe__reflet woot-orbe__reflet--bas';
    disque.appendChild(reflet);
    disque.appendChild(refletBas);

    const croix = document.createElement('span');
    croix.className = 'woot-lanceur__croix';

    const accent = document.createElement('div');
    accent.className = 'woot-orbe__accent';
    accent.innerHTML =
      '<svg viewBox="0 0 28 24" width="28" height="24" aria-hidden="true">' +
      '<path d="M5 2h18a4 4 0 0 1 4 4v9a4 4 0 0 1-4 4h-9l-6 5v-5H5a4 4 0 0 1-4-4V6a4 4 0 0 1 4-4z" fill="#fff" stroke="rgba(0,0,0,.12)"/>' +
      '<g fill="var(--client)"><circle cx="9" cy="10.5" r="1.6"/><circle cx="14" cy="10.5" r="1.6"/>' +
      '<circle cx="19" cy="10.5" r="1.6"/></g></svg>';

    conteneur.appendChild(disque);
    conteneur.appendChild(croix);
    conteneur.appendChild(accent);
  },

  // ORBE EST DU CSS PUR : les quatre gestes existent parce que le modele les appelle pour tous les
  // looks, et parce que certains — une bouche qui parle en SMIL — en ont besoin. Ici l'attribut
  // d'etat porte tout, et il n'y a rien a mettre en pause que la feuille n'arrete deja.
  gestes: {
    attention: () => {},
    ouvrir: () => {},
    fermer: () => {},
    reduireLeMouvement: () => {},
  },
};
